/**
 * Theme system for RetroPlay.
 *
 * Themes are CSS variable overrides gated by `<html data-theme="<id>">`.
 * The chosen theme persists to localStorage for anonymous users and syncs to
 * `user_settings.theme` via `PATCH /api/me/settings` for signed-in users.
 *
 * Contract for adding a new theme:
 *   1. Add an entry to THEMES below.
 *   2. Add a matching `html[data-theme="<id>"] { ... }` block in
 *      `public/styles/themes.css` (plus optional decoration layer) and a
 *      `.theme-thumb-<id>` swatch in `public/styles/theme-picker.css`.
 *   3. Add the id to the server-side allowlist in
 *      `src/server/routes/profile.js` (THEME_IDS) and a `theme:<id>`
 *      default in `src/server/modules.js` (the client treats a missing
 *      flag as disabled). That file is a pinned typography qualification
 *      input: without a re-qualification, use `config/features.json`.
 *   4. Add the id to the pre-paint OK allowlist in `public/index.html`
 *      and `apps/library/index.html` (mirror any randomWindow dates in
 *      ROLL_WINDOWS there).
 *   5. Optionally, a showcase template in `modules/theme-showcase.js`.
 *   6. A holiday theme also sets randomWindow. Its start is the day it takes
 *      over the Holiday choice, so it needs no picker entry of its own and
 *      its files stay in place after its season.
 * theme.test.js fails if any of the id lists in steps 3-4 drift.
 */

const LS_KEY = 'retroplay-theme';
const COOKIE_KEY = 'retroplay-theme';
const DEFAULT_ID = 'default';
/** The saved choice that follows the calendar. Never a data-theme value. */
export const HOLIDAY_ID = 'holiday';

/**
 * Persist the theme in both localStorage AND a long-lived cookie. The
 * cookie gives the <head> bootstrap script a second source of truth:
 * localStorage can be cleared per-origin (incognito, manual reset,
 * storage partitioning) while the cookie survives more cases. Server
 * code never reads this cookie — it's purely a client-side hint to
 * eliminate theme-flash on cold loads.
 */
function persistTheme(id) {
  try { localStorage.setItem(LS_KEY, id); } catch { /* ignore */ }
  try {
    // Max-Age = 1 year. SameSite=Lax is safe for same-origin reads.
    document.cookie = COOKIE_KEY + '=' + encodeURIComponent(id)
      + '; Path=/; Max-Age=31536000; SameSite=Lax';
  } catch { /* ignore */ }
}

/**
 * The saved choice: localStorage, then the cookie the <head> bootstrap also
 * reads. Either may be blocked, and a hand-edited cookie may not decode.
 */
function readSavedTheme() {
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (stored) return stored;
  } catch { /* ignore */ }
  try {
    const match = document.cookie.match(new RegExp('(?:^|; )' + COOKIE_KEY + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

/**
 * Single source of truth for what themes exist. The picker UI lists these,
 * with the holiday themes folded into one Holiday entry.
 *
 * @typedef {Object} ThemeEntry
 * @property {string} id           machine id, used as the data-theme attribute
 * @property {string} label        short display name (shown on the chip + in Settings)
 * @property {string} description  one-line description for the Settings card
 * @property {string} [thumbClass] CSS class for the preview swatch in the grid
 * @property {{start: number, end: number}} [randomWindow]  marks a holiday
 *                                 theme. Inclusive UTC MMDD dates: `start`
 *                                 is also the day it takes over the Holiday
 *                                 choice, and it holds until the next
 *                                 holiday's start. `end` bounds only the
 *                                 first-visit default. Holiday windows must
 *                                 not overlap.
 * @property {boolean} [seedOnly]  its flag is outside DEFAULTS, so the admin
 *                                 screen cannot change it.
 */

/** @type {readonly ThemeEntry[]} */
export const THEMES = Object.freeze([
  Object.freeze({
    id: 'default',
    label: 'Aurora',
    description: 'Mint and cyan on deep navy, the original RetroPlay palette.',
    thumbClass: 'theme-thumb-default',
  }),
  Object.freeze({
    id: 'trinitron',
    label: 'Trinitron',
    description: 'Broadcast studio: RGB sub-pixel grill, SMPTE signal blue.',
    thumbClass: 'theme-thumb-trinitron',
  }),
  Object.freeze({
    id: 'phosphor',
    label: 'Phosphor',
    description: 'Amber CRT monitor: monochrome warmth, heavy scanlines.',
    thumbClass: 'theme-thumb-phosphor',
  }),
  Object.freeze({
    id: 'cartridge',
    label: 'Cartridge Print',
    description: 'Paper cream with halftone dots, like a 1985 game box.',
    thumbClass: 'theme-thumb-cartridge',
  }),
  Object.freeze({
    id: 'gameboy',
    label: 'Game Boy',
    description: 'Putty shell and olive dot-matrix LCD, a DMG-01 in your browser.',
    thumbClass: 'theme-thumb-gameboy',
  }),
  Object.freeze({
    id: 'arcade',
    label: 'Neon Arcade',
    description: 'Hot pink and cyan on a perspective grid floor, 1985 cabinet energy.',
    thumbClass: 'theme-thumb-arcade',
  }),
  Object.freeze({
    id: 'modular',
    label: 'Modular Panel',
    description: 'Instrument-grade gunmetal with a persimmon accent and panel LEDs.',
    thumbClass: 'theme-thumb-modular',
  }),
  Object.freeze({
    id: 'swiss',
    label: 'Neo Swiss',
    description: 'Near-white editorial grid with a single red accent, magazine calm.',
    thumbClass: 'theme-thumb-swiss',
  }),
  Object.freeze({
    id: 'woodgrain',
    label: 'Heavy Sixer',
    description: 'Atari 2600: faux-teak woodgrain, orange trim, chrome switches.',
    thumbClass: 'theme-thumb-woodgrain',
  }),
  Object.freeze({
    id: 'frontloader',
    label: 'Front-Loader',
    description: 'NES-001: gray plastic body with the iconic red-and-black stripe.',
    thumbClass: 'theme-thumb-frontloader',
  }),
  Object.freeze({
    id: 'super',
    label: 'Super Power',
    description: 'Super NES: lavender-gray shell with deep purple buttons.',
    thumbClass: 'theme-thumb-super',
  }),
  Object.freeze({
    id: 'blueprint',
    label: 'Blueprint',
    description: 'Prussian-blue drafting paper with orthogonal console schematics.',
    thumbClass: 'theme-thumb-blueprint',
  }),
  Object.freeze({
    id: 'matrix',
    label: 'Matrix',
    description: 'Phosphor green on black with falling digital rain behind content.',
    thumbClass: 'theme-thumb-matrix',
  }),
  Object.freeze({
    id: 'cyberpunk',
    label: 'Cyberpunk',
    description: 'Tech-noir violet-black with hot pink and electric cyan accents.',
    thumbClass: 'theme-thumb-cyberpunk',
  }),
  Object.freeze({
    id: 'wasteland',
    label: 'Wasteland',
    description: 'Post-apocalyptic Pip-Boy terminal, neon green on olive-black.',
    thumbClass: 'theme-thumb-wasteland',
  }),
  Object.freeze({
    id: 'steampunk',
    label: 'Steampunk',
    description: 'Victorian clockwork: brass gears, copper pipes, and parchment warmth.',
    thumbClass: 'theme-thumb-steampunk',
  }),
  Object.freeze({
    id: 'halloween',
    label: "Jack-o'-Lantern",
    description: 'Candlelight on a plum-black night, with pumpkins carved like controllers.',
    thumbClass: 'theme-thumb-halloween',
    randomWindow: Object.freeze({ start: 1001, end: 1101 }),
  }),
  Object.freeze({
    id: 'thanksgiving',
    label: 'Hand Turkey',
    description: 'Goldenrod construction paper, crayon, and a hand turkey on the board.',
    thumbClass: 'theme-thumb-thanksgiving',
    randomWindow: Object.freeze({ start: 1106, end: 1127 }),
    seedOnly: true,
  }),
]);

const PAINT_IDS = new Set(THEMES.map((t) => t.id));
const VALID_IDS = new Set([...PAINT_IDS, HOLIDAY_ID]);

const isHolidayTheme = (id) => THEMES.some((t) => t.id === id && t.randomWindow);
// Every holiday theme is saved as the slot.
const savedId = (id) => (isHolidayTheme(id) ? HOLIDAY_ID : id);

// Optional feature-flag filter hook, wired by setThemeFlagSource() once the
// server's flags arrive. Unwired, every theme counts as enabled: the picker
// is never empty during boot, and a failed flag fetch can't veto a theme.
let _isFlagEnabled = null;

/**
 * Wire in the feature-flag check used by getEnabledThemes().
 * Low-level hook; main.js wires the module registry through
 * setThemeFlagSource() instead.
 * @param {(flagName: string) => boolean} checkFn
 */
export function setThemeFlagChecker(checkFn) {
  _isFlagEnabled = (typeof checkFn === 'function') ? checkFn : null;
}

/**
 * Wire the theme flags to the module registry. Called once from main.js
 * after moduleRegistry.load() resolves.
 *
 * Only a flag set the server actually sent is wired, and it is fail-closed:
 * a `theme:<id>` it omits, or sets to anything but true, is disabled. After
 * a failed fetch the check stays unwired instead, because getFeatures() then
 * holds no server flags, every theme would read as disabled, and
 * reconcileTheme() would persist the default over the visitor's saved theme.
 *
 * @param {{ hasServerFlags(): boolean, getFeatures(): Record<string, unknown> }} registry
 */
export function setThemeFlagSource(registry) {
  setThemeFlagChecker(registry.hasServerFlags()
    ? (name) => registry.getFeatures()[name] === true
    : null);
}

/** Per-theme flag check. Holiday logic goes through isThemeEnabled. */
function isFlagOn(id) {
  if (!_isFlagEnabled) return true;               // flags pending or unverified: allow
  if (id === DEFAULT_ID) return true;             // Aurora always available
  return _isFlagEnabled(`theme:${id}`);
}

/**
 * The holiday the Holiday choice paints: the enabled holiday theme with the
 * latest start on or before today, wrapping to the latest start of the year.
 * Null when no holiday theme is enabled. The pre-paint scripts mirror this
 * without the flag check.
 *
 * @param {Date} [now]
 * @returns {string | null}
 */
export function resolveHolidayTheme(now = new Date()) {
  const today = (now.getUTCMonth() + 1) * 100 + now.getUTCDate();
  const holidays = THEMES.filter((t) => t.randomWindow && isFlagOn(t.id))
    .sort((a, b) => a.randomWindow.start - b.randomWindow.start);
  if (holidays.length === 0) return null;
  const started = holidays.filter((t) => t.randomWindow.start <= today);
  return (started.at(-1) || holidays.at(-1)).id;
}

// The theme a saved choice paints: holiday ids follow the calendar.
const paintId = (id) => ((id === HOLIDAY_ID || isHolidayTheme(id))
  ? resolveHolidayTheme() || DEFAULT_ID
  : id);

/**
 * The picker list: enabled non-holiday themes, then one Holiday entry
 * showing the active holiday. If no flag checker is wired, or the flag cache
 * isn't loaded yet, every theme is considered enabled — the chip never
 * renders empty.
 *
 * Default (Aurora) is always included as a hard fallback, so disabling
 * every other flag still leaves users with at least one choice.
 *
 * @param {Date} [now]
 * @returns {readonly ThemeEntry[]}
 */
export function getEnabledThemes(now = new Date()) {
  const themes = THEMES.filter((t) => !t.randomWindow && isFlagOn(t.id));
  const active = resolveHolidayTheme(now);
  if (!active) return themes;
  const holiday = getThemeEntry(active);
  return [...themes, {
    id: HOLIDAY_ID,
    label: 'Holiday',
    description: `Now showing ${holiday.label}. Follows the calendar.`,
    thumbClass: holiday.thumbClass,
  }];
}

/** Fast single-id enabled check used by reconcileTheme + hydrateFromServer. */
export function isThemeEnabled(id) {
  if (id === HOLIDAY_ID || isHolidayTheme(id)) return resolveHolidayTheme() !== null;
  return isFlagOn(id);
}

/**
 * If the currently-applied theme has been disabled, fall back to the
 * default. A holiday theme that is no longer the active holiday repaints to
 * the one that is, without touching the saved choice. Called when feature
 * flags load (first fetch) or change (admin toggle), and when a tab becomes
 * visible again. Safe to call before flags are loaded.
 *
 * A switch is announced on `retroplay:theme-changed` like every other
 * theme change, so the showcase and the pickers never keep showing the
 * old theme's markup under the new palette.
 *
 * @returns {boolean} true when it changed the painted theme
 */
export function reconcileTheme() {
  if (typeof document === 'undefined') return false;
  const current = getTheme();
  if (!isThemeEnabled(current)) {
    applyTheme(DEFAULT_ID);
    persistTheme(DEFAULT_ID);
    announceTheme();
    return true;
  }
  if (paintId(current) === current) return false;
  applyTheme(current);
  announceTheme();
  return true;
}

function announceTheme() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('retroplay:theme-changed', { detail: { id: getTheme() } }));
}

/** @returns {string} the id of the currently applied theme */
export function getTheme() {
  const attr = (typeof document !== 'undefined' && document.documentElement.dataset.theme) || '';
  return PAINT_IDS.has(attr) ? attr : DEFAULT_ID;
}

/** @returns {string} the id the pickers mark as active */
export function getPickerThemeId() {
  return savedId(getTheme());
}

/**
 * Get the ThemeEntry for an id (or the default if unknown).
 * @param {string} id
 * @returns {ThemeEntry}
 */
export function getThemeEntry(id) {
  return THEMES.find((t) => t.id === id) || THEMES[0];
}

/**
 * Apply a theme (pure DOM write, no persistence).
 * Unknown ids are coerced to DEFAULT_ID so the UI never ends up in a broken state.
 * @param {string} id
 */
export function applyTheme(id) {
  if (typeof document === 'undefined') return;
  const safe = VALID_IDS.has(id) ? paintId(id) : DEFAULT_ID;
  document.documentElement.dataset.theme = safe;
}

/**
 * Apply a theme and persist the choice.
 * @param {string} id
 * @param {{ sync?: boolean }} [opts]  sync=true pushes the value to the server
 */
export function setTheme(id, opts = {}) {
  if (!VALID_IDS.has(id)) {
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(`[theme] unknown theme "${id}", ignoring`);
    }
    return;
  }
  applyTheme(id);
  persistTheme(savedId(id));
  if (opts.sync) {
    _syncToServer(savedId(id));
  }
}

/**
 * Pick the seasonal default inside its inclusive UTC window, otherwise a
 * random non-seasonal theme. Used for first visits without a saved choice
 * and as a fallback if the bootstrap script is missing.
 */
export function pickRandomTheme(now = new Date()) {
  const today = (now.getUTCMonth() + 1) * 100 + now.getUTCDate();
  const seasonal = THEMES.find((t) => t.randomWindow
    && today >= t.randomWindow.start && today <= t.randomWindow.end);
  if (seasonal) return seasonal.id;
  const ids = THEMES.filter((t) => !t.randomWindow).map((t) => t.id);
  return ids[Math.floor(Math.random() * ids.length)] || DEFAULT_ID;
}

/**
 * Initialize the theme on app startup. Called once from main.js.
 *
 * Behavior:
 *  - If the user has made a manual pick (localStorage populated), honor it.
 *    This is the "sticky" case — user owns their theme.
 *  - Otherwise, respect whatever the <head> bootstrap script already set
 *    (which should be a valid random theme). This avoids a double random-roll
 *    that would flash a different theme between paint and script execution.
 *  - As a last-ditch fallback (if the bootstrap script didn't run or set an
 *    invalid value), pick a fresh random theme.
 *
 * Random first-visit choice is NOT persisted to localStorage — it stays
 * "ephemeral" until the user clicks a theme option, at which point
 * setTheme() saves the choice and it becomes sticky.
 */
export function initTheme() {
  if (typeof document === 'undefined') return;
  const stored = readSavedTheme();
  // Sticky manual choice wins.
  if (stored && VALID_IDS.has(stored)) {
    applyTheme(stored);
    return;
  }
  // No stored choice — respect the bootstrap script's random pick if valid.
  const current = document.documentElement.dataset.theme;
  if (current && PAINT_IDS.has(current)) return;
  // Bootstrap didn't run or set an invalid value; pick fresh.
  applyTheme(pickRandomTheme());
}

/**
 * Hydrate the theme from a server-side user settings payload.
 * Called from the sign-in flow in auth.js.
 *
 * Contract:
 *  - If the server has a value, server wins — apply + mirror to localStorage.
 *  - If the server is null and we have a localStorage value, push localStorage
 *    up to the server (one-shot migration on first sign-in).
 *  - If both are null, no-op.
 *
 * @param {{ theme?: string | null } | null | undefined} settings
 */
export function hydrateFromServer(settings) {
  if (!settings) return;
  const serverTheme = settings.theme;
  // Server value wins ONLY if the theme exists in the catalog AND the
  // admin hasn't disabled it since it was saved. A disabled theme on
  // the server means the user's last pick was vetoed — fall through to
  // localStorage or the default.
  if (serverTheme && VALID_IDS.has(serverTheme) && isThemeEnabled(serverTheme)) {
    applyTheme(serverTheme);
    persistTheme(savedId(serverTheme));
    announceTheme();
    return;
  }
  // Server has no value — push whatever the client is using, if anything.
  const local = readSavedTheme();
  if (local && VALID_IDS.has(local) && local !== DEFAULT_ID) {
    _syncToServer(savedId(local));
  }
}

/**
 * Fire-and-forget PATCH to /api/me/settings with one silent retry.
 * Errors are swallowed — the client state remains authoritative locally.
 */
function _syncToServer(id) {
  if (typeof fetch === 'undefined') return;
  const payload = JSON.stringify({ theme: id });
  const attempt = (retries) => {
    fetch('/api/me/settings', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: payload,
    }).catch(() => {
      if (retries > 0) setTimeout(() => attempt(retries - 1), 1500);
    });
  };
  attempt(1);
}

// Test hook — export private helpers in test env only.
export const __test__ = { LS_KEY, DEFAULT_ID, VALID_IDS, PAINT_IDS };
